import React, { useEffect, useState, useRef } from 'react';
interface StickyNoteProps {
  triggerSlideUp?: boolean;
}
export function StickyNote({
  triggerSlideUp = false
}: StickyNoteProps) {
  const [position, setPosition] = useState({
    x: 0,
    y: 0
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({
    x: 0,
    y: 0
  });
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const noteRef = useRef<HTMLDivElement>(null);
  // Auto-hide after 20 seconds
  useEffect(() => {
    const hideTimer = setTimeout(() => {
      setIsVisible(false);
    }, 20000);
    return () => {
      clearTimeout(hideTimer);
    };
  }, []);
  // Set initial position to bottom right on mount
  useEffect(() => {
    const noteWidth = 256; // w-64 in pixels
    const noteHeight = 180; // approximate height with magnet
    const padding = 40; // padding from edges
    setPosition({
      x: window.innerWidth - noteWidth - padding,
      y: window.innerHeight - noteHeight - padding
    });
  }, []);
  // Animate with background slide-up
  useEffect(() => {
    if (triggerSlideUp && !isDragging) {
      setIsAnimating(true);
      // Reset animation after it completes (1.5s to match background)
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [triggerSlideUp, isDragging]);
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!noteRef.current) return;
    const rect = noteRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    setIsDragging(true);
  };
  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragOffset.x,
      y: e.clientY - dragOffset.y
    });
  };
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);
  if (!isVisible) return null;
  return <div ref={noteRef} onMouseDown={handleMouseDown} style={{
    position: 'fixed',
    left: `${position.x}px`,
    top: `${position.y}px`,
    cursor: isDragging ? 'grabbing' : 'grab',
    zIndex: 50,
    animation: isAnimating ? 'slideUpNote 1.5s ease-out' : 'none'
  }} className="hidden md:block select-none" data-id="element-182">
      <style data-id="element-183">{`
        @keyframes slideUpNote {
          from { 
            transform: translateY(100vh);
          }
          to { 
            transform: translateY(0);
          }
        }
      `}</style>

      {/* Airplane magnet at top */}
      <div className="relative" data-id="element-184">
        {/* Sticky note */}
        <div className="p-4 w-64 relative" style={{
        background: 'linear-gradient(180deg, #b8d8c8 0%, #e8f4f0 100%)',
        borderRadius: '4px',
        transform: 'rotate(3deg)',
        boxShadow: '0 10px 40px rgba(255, 255, 255, 0.3), 0 0 60px rgba(255, 255, 255, 0.2), 0 4px 6px rgba(0, 0, 0, 0.1)',
        backgroundImage: `
              repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.02) 2px, rgba(0,0,0,0.02) 3px),
              repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(0,0,0,0.02) 2px, rgba(0,0,0,0.02) 3px),
              url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.03'/%3E%3C/svg%3E"),
              linear-gradient(180deg, #b8d8c8 0%, #e8f4f0 100%)
            `
      }} data-id="element-185">
          <p className="text-xs text-black leading-snug mb-2" data-id="element-186">
            <span className="font-bold" data-id="element-187">POST-STRANGERS</span> returns to Hong
            Kong and Chiang Mai in February 2026.
          </p>
          <p className="text-xs text-black leading-snug" data-id="element-188">
            Get notified about upcoming dinners{' '}
            <a href="#" className="underline font-medium" data-id="element-189">
              here
            </a>
            .
          </p>
        </div>

        {/* Airplane magnet */}
        <div className="absolute left-1/2" style={{
        top: '-1.0rem',
        transform: 'translateX(-50%) rotate(-2deg)',
        zIndex: 10
      }} data-id="element-190">
          <img src="https://cdn.magicpatterns.com/uploads/gKCSAUNLGE8HoehVzgsuDN/plane.png" alt="Airplane" className="w-10 h-10" style={{
          filter: 'drop-shadow(0 3px 6px rgba(0, 0, 0, 0.3))'
        }} data-id="element-191" />
        </div>
      </div>
    </div>;
}